import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useRef, useState } from 'react';
import { getAgentElement } from './getAgentElement';
import { startNewThreadWithMessage } from './threads/startNewThreadWithMessage';
import { updateExtraSituationalAwarenessContext } from './situationalAwareness/useExtraSituationalAwarenessContext';
export function useOpenMindsetAgent(agentElementId, addAgentIfRequired) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasInitialized = _useState2[0],
    setHasInitialized = _useState2[1];
  var _useState3 = useState('loading'),
    _useState4 = _slicedToArray(_useState3, 2),
    loadingStatus = _useState4[0],
    setLoadingStatus = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isOpen = _useState6[0],
    setIsOpen = _useState6[1];

  // Bumped when an init gives up on a container that was swapped out from
  // under it. Nothing else changes on that path, so a panel that remounted
  // while the attempt was still in flight would otherwise sit on the spinner
  // forever - the auto-init effect has no dependency left to re-run on.
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    abortedInitCount = _useState8[0],
    setAbortedInitCount = _useState8[1];

  // A ref, not state: hasInitialized wouldn't update in time to stop a second
  // ensureInitialized() call in the same commit, which would tear down the
  // <mindset-agent> element the first call just created.
  var initPromiseRef = useRef(null);

  // Also called directly by MindsetAgentDock's sidebar auto-init, since handleOpen's
  // setIsOpen(true) would otherwise stick the sidebar open after a desktop-to-mobile resize.
  var ensureInitialized = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var promise;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          if (!hasInitialized) {
            _context2.next = 1;
            break;
          }
          return _context2.abrupt("return", true);
        case 1:
          if (!initPromiseRef.current) {
            _context2.next = 2;
            break;
          }
          return _context2.abrupt("return", initPromiseRef.current);
        case 2:
          promise = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
            var container, _t;
            return _regeneratorRuntime.wrap(function (_context) {
              while (1) switch (_context.prev = _context.next) {
                case 0:
                  setLoadingStatus('loading');
                  container = document.getElementById(agentElementId);
                  _context.prev = 1;
                  _context.next = 2;
                  return addAgentIfRequired();
                case 2:
                  if (getAgentElement(agentElementId)) {
                    _context.next = 3;
                    break;
                  }
                  // A different container means the panel has already
                  // remounted and is waiting on this attempt, so ask for a
                  // fresh one. An unchanged container means the SDK simply
                  // never appended an agent (e.g. it is not configured for
                  // this hub), where retrying would just spin.
                  if (document.getElementById(agentElementId) !== container) {
                    setAbortedInitCount(function (count) {
                      return count + 1;
                    });
                  }
                  return _context.abrupt("return", false);
                case 3:
                  setHasInitialized(true);
                  setLoadingStatus('ready');
                  return _context.abrupt("return", true);
                case 4:
                  _context.prev = 4;
                  _t = _context["catch"](1);
                  setLoadingStatus('error');
                  return _context.abrupt("return", false);
                case 5:
                  _context.prev = 5;
                  initPromiseRef.current = null;
                  return _context.finish(5);
                case 6:
                case "end":
                  return _context.stop();
              }
            }, _callee, null, [[1, 4, 5, 6]]);
          }))();
          initPromiseRef.current = promise;
          return _context2.abrupt("return", promise);
        case 3:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  })), [hasInitialized, addAgentIfRequired, agentElementId]);
  var handleOpen = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    var message,
      silent,
      threadTitle,
      coachingContext,
      _args3 = arguments;
    return _regeneratorRuntime.wrap(function (_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          message = _args3.length > 0 && _args3[0] !== undefined ? _args3[0] : '';
          silent = _args3.length > 1 && _args3[1] !== undefined ? _args3[1] : false;
          threadTitle = _args3.length > 2 && _args3[2] !== undefined ? _args3[2] : '';
          coachingContext = _args3.length > 3 && _args3[3] !== undefined ? _args3[3] : '';
          // Silent messages (e.g. background priming) shouldn't surface the panel, as e.g. it would open unexpectedly on mobile.
          if (!silent) {
            setIsOpen(true);
          }

          // Set before any await so useMindsetSituationalAwareness's resync on the
          // init re-render picks it up rather than firing first and clearing it.
          // Left in place afterward so it persists while the AI responds.
          updateExtraSituationalAwarenessContext({
            coachingContext: coachingContext || undefined
          });
          _context3.next = 1;
          return ensureInitialized();
        case 1:
          if (_context3.sent) {
            _context3.next = 2;
            break;
          }
          // Clear so it doesn't leak into other surfaces if init fails
          // before the agent is ever shown.
          updateExtraSituationalAwarenessContext({
            coachingContext: undefined
          });
          return _context3.abrupt("return");
        case 2:
          _context3.next = 3;
          return startNewThreadWithMessage(agentElementId, {
            message: message,
            silent: silent,
            threadTitle: threadTitle,
            coachingContext: coachingContext,
            onSent: function onSent() {
              return setLoadingStatus('ready');
            }
          });
        case 3:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  })), [ensureInitialized, agentElementId]);
  return {
    loadingStatus: loadingStatus,
    setLoadingStatus: setLoadingStatus,
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    hasInitialized: hasInitialized,
    abortedInitCount: abortedInitCount,
    ensureInitialized: ensureInitialized,
    handleOpen: handleOpen
  };
}